import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { jobApi } from '../../api/jobApi'
import { Job } from '../../types'

interface JobState {
  feed: Job[]
  feedPage: number
  feedHasMore: boolean
  feedLoading: boolean
  error: string | null
}

const initialState: JobState = {
  feed: [],
  feedPage: 0,
  feedHasMore: true,
  feedLoading: false,
  error: null,
}

export const fetchFeed = createAsyncThunk(
  'jobs/fetchFeed',
  async ({ page = 0, size = 20, search = '' }: { page?: number; size?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await jobApi.getFeed(page, size, search)
      const data = response.data.data
      const jobs = data?.content ?? []
      return { jobs, page, last: data?.last ?? true }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } }
      return rejectWithValue(error.response?.data?.message ?? 'Failed to load feed')
    }
  }
)

export const appendFeed = createAsyncThunk(
  'jobs/appendFeed',
  async ({ page, size = 20, search = '' }: { page: number; size?: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await jobApi.getFeed(page, size, search)
      const data = response.data.data
      const jobs = data?.content ?? []
      return { jobs, page, last: data?.last ?? true }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } }
      return rejectWithValue(error.response?.data?.message ?? 'Failed to load more')
    }
  }
)

const jobSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    resetFeed: (state) => {
      state.feed = []
      state.feedPage = 0
      state.feedHasMore = true
    },
    toggleLikeInFeed: (state, action) => {
      const { jobId, liked, likesCount } = action.payload
      const job = state.feed.find((j) => j.id === jobId)
      if (job) {
        job.likedByMe = liked
        job.likesCount = likesCount
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeed.pending, (state) => { state.feedLoading = true; state.error = null })
      .addCase(fetchFeed.fulfilled, (state, action) => {
        state.feedLoading = false
        const { jobs, page, last } = action.payload
        state.feed = jobs
        state.feedPage = page
        state.feedHasMore = !last
      })
      .addCase(fetchFeed.rejected, (state, action) => {
        state.feedLoading = false
        state.error = action.payload as string
      })
      .addCase(appendFeed.pending, (state) => { state.feedLoading = true })
      .addCase(appendFeed.fulfilled, (state, action) => {
        state.feedLoading = false
        const { jobs, page, last } = action.payload
        state.feed = [...state.feed, ...jobs]
        state.feedPage = page
        state.feedHasMore = !last
      })
      .addCase(appendFeed.rejected, (state) => { state.feedLoading = false })
  },
})

export const { resetFeed, toggleLikeInFeed } = jobSlice.actions
export default jobSlice.reducer
